/**
 * HESABIM — profesyonel hesap merkezi. Sidebar'daki profil kartına tıklanınca açılır.
 * Profil + paket + TANIMLI TOKEN hakkı + motor motor kullanım dökümü + paket özellik kotaları
 * ("20/100 içerik kullanıldı") + token fiyat listesi + kendi API anahtarları + abonelik.
 * Kullanıcıya DOLAR gösterilmez: 1 birim maliyet = 1000 token.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { ApiKeysPanel } from "@/components/api-keys-panel";
import { ENGINE_PRICES } from "@/components/cost-confirm";
import { getStoredUser, logout } from "@/lib/auth";
import { useMe } from "@/hooks/use-me";
import { Coins, User as UserIcon, Package, CreditCard, ShieldCheck, LogOut, Loader2, Gauge, ListChecks, Users, Plus, X } from "lucide-react";

type Quota = { key: string; label: string; used: number; limit: number | null };
type Breakdown = { engine: string; usd30: number; runs30: number; usdTotal: number };
type Account = {
  profile: { id: number; email: string; name: string; isAdmin: boolean; createdAt: string; subscriptionActive: boolean; emailVerified: boolean };
  package: { name: string; siteLimit: number; tokenBudgetUsd: number; priceMonthly: number; description: string;
    features: { contentPerSite: number; strengthenPerSite: number; discoveryPerSite: number; geoPerSite: number; auditPerSite: number; assistantVoice: boolean; byok: boolean } } | null;
  domains: number; usage30: number; usageTotal: number; breakdown: Breakdown[]; quotas: Quota[];
};

const TOKENS_PER_UNIT = 1000;
const tok = (usd: number) => Math.round(usd * TOKENS_PER_UNIT).toLocaleString("tr-TR");
const ENGINE_LABELS: Record<string, string> = {
  assistant: "Geolop Asistanı", writer: "İçerik Üretimi", discovery: "İçerik Keşfi",
  tech: "Teknik Denetim", backlink: "Backlink Denetimi", "action-plan": "Aksiyon Planı",
};

export default function Account() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const stored = getStoredUser();
  const { data: ben } = useMe();          // Google profil fotoğrafı buradan
  const [avatarHata, setAvatarHata] = useState(false);
  const [name, setName] = useState("");
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [saving, setSaving] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["me-account"],
    queryFn: async (): Promise<Account | null> => { const r = await fetch("/api/me/account"); return r.ok ? r.json() : null; },
  });

  const saveProfile = async () => {
    const body: Record<string, string> = {};
    if (name.trim()) body.name = name.trim();
    if (newPw) { body.currentPassword = curPw; body.newPassword = newPw; }
    if (!Object.keys(body).length) return;
    setSaving(true);
    try {
      const r = await fetch("/api/me/profile", { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      toast(r.ok ? { title: "Profil güncellendi" } : { title: "Güncellenemedi", description: d.error, variant: "destructive" });
      if (r.ok) { setName(""); setCurPw(""); setNewPw(""); queryClient.invalidateQueries({ queryKey: ["me-account"] }); }
    } finally { setSaving(false); }
  };

  const [brandName, setBrandName] = useState("");
  const [brandLogo, setBrandLogo] = useState("");
  const [brandColor, setBrandColor] = useState("#e83e3c");
  const [brandSaving, setBrandSaving] = useState(false);
  const [delConfirm, setDelConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const cancelSub = async () => {
    if (!window.confirm("Aboneliğin iptal edilecek ve oturumun kapanacak. Emin misin?")) return;
    const r = await fetch("/api/me/cancel-subscription", { method: "POST" });
    if (r.ok) { window.alert("Aboneliğin iptal edildi."); logout(); }
    else toast({ title: "İptal edilemedi", description: ((await r.json().catch(() => ({}))) as { error?: string }).error, variant: "destructive" });
  };

  if (isLoading) return <div className="p-10 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline" /> Yükleniyor…</div>;
  if (!data) return <div className="p-10 text-center text-muted-foreground">Hesap bilgileri alınamadı.</div>;

  const p = data.package;
  const displayName = data.profile.name?.trim() || stored?.name || data.profile.email;
  const initials = displayName.split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  const usedTok = data.usage30 * TOKENS_PER_UNIT;
  const capTok = p ? p.tokenBudgetUsd * TOKENS_PER_UNIT : null;
  const pct = capTok ? Math.min(100, Math.round((usedTok / capTok) * 100)) : 0;
  const barColor = pct >= 90 ? "#d92d20" : pct >= 70 ? "#d97706" : "#16a34a";
  const inputCls = "border rounded-lg px-2.5 py-1.5 text-[13px] bg-white";
  const topEngines = [...data.breakdown].filter((b) => b.usd30 > 0).sort((a, b) => b.usd30 - a.usd30);

  return (
    <div className="p-4 md:p-6 max-w-(--breakpoint-2xl) mx-auto space-y-5">
      {/* ── Profil başlığı ── */}
      {/* Arka plan sabit AÇIK gradient'ti — koyu temada beyaz kutu olarak patlıyordu.
          Tema değişkenli marka tonuna çevrildi (token onay popup'ıyla aynı düzeltme). */}
      <Card className="border-brand-500/40 bg-brand-50/60 dark:bg-brand-500/[0.08]">
        <CardContent className="p-6 flex items-center gap-4 flex-wrap">
          {ben?.avatarUrl && !avatarHata ? (
            <img src={ben.avatarUrl} alt="" referrerPolicy="no-referrer" onError={() => setAvatarHata(true)}
              className="h-[60px] w-[60px] shrink-0 rounded-full object-cover ring-1 ring-black/10 dark:ring-white/15" />
          ) : (
            <div style={{ width: 60, height: 60, borderRadius: "50%", background: "linear-gradient(135deg,#e83e3c,#ec6461)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
              {initials || "?"}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold">{displayName}</h1>
              {data.profile.isAdmin && <span className="bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-warning-400 text-[10px] font-bold px-2 py-0.5 rounded-full" >ADMIN</span>}
              {p && <span className="bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-400 text-[11px] font-semibold px-2.5 py-0.5 rounded-full" >{p.name} paketi</span>}
              {!p && <span className="bg-gray-100 text-gray-500 dark:bg-white/5 dark:text-gray-400 text-[11px] font-semibold px-2.5 py-0.5 rounded-full" >Paketsiz (sınırsız)</span>}
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">{data.profile.email} · üyelik {new Date(data.profile.createdAt).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] px-2 py-1 rounded-full inline-flex items-center gap-1"
              style={data.profile.subscriptionActive ? { background: "#dcfce7", color: "#039855" } : { background: "#fee2e2", color: "#b42318" }}>
              <ShieldCheck className="w-3 h-3" /> {data.profile.subscriptionActive ? "Abonelik aktif" : "Abonelik durduruldu"}
            </span>
            <button onClick={() => logout()} title="Çıkış yap" className="p-2 rounded-lg hover:bg-muted text-muted-foreground"><LogOut className="w-4 h-4" /></button>
          </div>
        </CardContent>
      </Card>

      {/* ── Token hakkı ── */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <div className="font-bold flex items-center gap-1.5"><Coins className="w-4 h-4 text-warning-700 dark:text-warning-400" /> Token Hakkı</div>
            <span className="text-[11px] text-muted-foreground">son 30 günlük kullanım</span>
          </div>
          {!p ? (
            <p className="text-[13px] text-muted-foreground">Hesabın paketsiz — token kullanımı sınırsız. Şimdiye kadar toplam <strong>{tok(data.usageTotal)} token</strong> kullandın.</p>
          ) : (
            <>
              <div className="flex items-end gap-2 mb-1">
                <span className="text-3xl font-bold" style={{ color: barColor }}>{tok(data.usage30)}</span>
                <span className="text-sm text-muted-foreground mb-1">/ {tok(p.tokenBudgetUsd)} token</span>
              </div>
              <div className="bg-gray-100 dark:bg-gray-800 h-3 rounded-full overflow-hidden" >
                <div className="h-3 rounded-full transition-all" style={{ width: `${pct}%`, background: barColor }} />
              </div>
              <div className="flex justify-between text-[11px] mt-1.5">
                <span className="text-muted-foreground">Kalan: <strong>{tok(Math.max(0, p.tokenBudgetUsd - data.usage30))} token</strong></span>
                <span className="text-muted-foreground">Toplam kullanım: {tok(data.usageTotal)} token</span>
              </div>
              {pct >= 90 && (
                <div className="bg-error-50 text-error-700 dark:bg-error-500/15 dark:text-error-400 mt-2 text-[12px] rounded-lg px-3 py-2" >
                  Token hakkının %{pct}'i kullanıldı. Hak dolduğunda içerik üretimi, keşif ve analiz motorları duraklatılır — paketini yükseltebilirsin.
                </div>
              )}
            </>
          )}

          {/* Motor bazlı döküm */}
          {topEngines.length > 0 && (
            <div className="mt-4">
              <div className="text-[11px] font-semibold text-muted-foreground mb-1.5">TOKEN NEREYE GİTTİ (30 gün)</div>
              <div className="space-y-1">
                {topEngines.map((b) => {
                  const share = data.usage30 > 0 ? Math.round((b.usd30 / data.usage30) * 100) : 0;
                  return (
                    <div key={b.engine} className="flex items-center gap-2">
                      <span className="text-[12px] w-40 shrink-0 truncate">{ENGINE_LABELS[b.engine] ?? b.engine}</span>
                      <div className="bg-gray-100 dark:bg-gray-800 flex-1 h-2 rounded-full" >
                        <div className="h-2 rounded-full" style={{ width: `${share}%`, background: "#e83e3c" }} />
                      </div>
                      <span className="text-[11px] text-muted-foreground w-32 text-right shrink-0">{tok(b.usd30)} token · {b.runs30} işlem</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Paket özellikleri / kotalar ── */}
      <Card>
        <CardContent className="p-6">
          <div className="font-bold flex items-center gap-1.5 mb-1"><Package className="w-4 h-4 text-warning-700 dark:text-warning-400" /> Paket Özelliklerin</div>
          {!p ? (
            <p className="text-[13px] text-muted-foreground">Paketsiz hesap — tüm özellikler sınırsız. Paketler site başına ayda 20 içerik, 10 güçlendirme, keşif ve GEO takibi içerir.</p>
          ) : (
            <>
              <p className="text-[12px] text-muted-foreground mb-3">{p.description}</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="border-gray-200 dark:border-gray-800 rounded-xl border p-3" >
                  <div className="flex justify-between text-[12px]"><span className="text-muted-foreground">Kayıtlı site</span><span className="font-semibold">{data.domains} / {p.siteLimit}</span></div>
                  <div className="bg-gray-100 dark:bg-gray-800 h-1.5 rounded-full mt-1.5" >
                    <div className="h-1.5 rounded-full" style={{ width: `${Math.min(100, (data.domains / p.siteLimit) * 100)}%`, background: data.domains >= p.siteLimit ? "#d92d20" : "#ec6461" }} />
                  </div>
                </div>
                {data.quotas.map((qt) => {
                  const lim = qt.limit ?? 0;
                  const qpct = lim ? Math.min(100, Math.round((qt.used / lim) * 100)) : 0;
                  return (
                    <div key={qt.key} className="border-gray-200 dark:border-gray-800 rounded-xl border p-3" >
                      <div className="flex justify-between text-[12px]"><span className="text-muted-foreground">{qt.label}</span><span className="font-semibold">{qt.used} / {lim}</span></div>
                      <div className="bg-gray-100 dark:bg-gray-800 h-1.5 rounded-full mt-1.5" >
                        <div className="h-1.5 rounded-full" style={{ width: `${qpct}%`, background: qpct >= 100 ? "#d92d20" : qpct >= 70 ? "#d97706" : "#16a34a" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {p.features.assistantVoice && <span className="bg-success-50 text-success-600 dark:bg-success-500/15 dark:text-success-500 text-[11px] px-2 py-1 rounded-full" >✓ Sesli Geolop Asistanı</span>}
                {p.features.byok && <span className="bg-success-50 text-success-600 dark:bg-success-500/15 dark:text-success-500 text-[11px] px-2 py-1 rounded-full" >✓ Kendi API anahtarını kullanma</span>}
                <span className="bg-success-50 text-success-600 dark:bg-success-500/15 dark:text-success-500 text-[11px] px-2 py-1 rounded-full" >✓ Tüm ajanlar ve raporlar</span>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Token fiyat listesi (şeffaflık) ── */}
      <Card>
        <CardContent className="p-6">
          <div className="font-bold flex items-center gap-1.5 mb-2"><ListChecks className="w-4 h-4 text-warning-700 dark:text-warning-400" /> İşlem Başına Token</div>
          <p className="text-[12px] text-muted-foreground mb-3">Hangi işlemin ne kadar token harcadığını buradan planlayabilirsin. Her işlem başlamadan önce onay ekranında da gösterilir.</p>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
            {Object.entries(ENGINE_PRICES).sort((a, b) => b[1].tokens - a[1].tokens).map(([k, v]) => (
              <div key={k} className="border-gray-100 dark:border-gray-800 flex justify-between text-[12px] border-b py-1.5" >
                <span>{v.title}</span>
                <span className="font-semibold text-warning-700 dark:text-warning-400">~{v.tokens.toLocaleString("tr-TR")}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── Profil ayarları ── */}
      <Card>
        <CardContent className="p-6 space-y-3">
          <div className="font-bold flex items-center gap-1.5"><UserIcon className="w-4 h-4 text-warning-700 dark:text-warning-400" /> Profil Ayarları</div>
          <div className="flex flex-wrap gap-2 items-center">
            <input className={inputCls} placeholder="Görünen isim" value={name} onChange={(e) => setName(e.target.value)} style={{ width: 180 }} />
            <input className={inputCls} type="password" placeholder="Mevcut parola" value={curPw} onChange={(e) => setCurPw(e.target.value)} style={{ width: 160 }} autoComplete="current-password" />
            <input className={inputCls} type="password" placeholder="Yeni parola" value={newPw} onChange={(e) => setNewPw(e.target.value)} style={{ width: 160 }} autoComplete="new-password" />
            <Button size="sm" className="bg-brand-500 hover:bg-brand-600 h-9" disabled={saving} onClick={() => void saveProfile()} >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Kaydet"}
            </Button>
          </div>
          <div className="text-[11px] text-muted-foreground">Parolan tek yönlü şifrelenir; kimse (yönetici dahil) göremez — yalnız değiştirilebilir.</div>
        </CardContent>
      </Card>

      {/* ── Ekip ── */}
      <Card><CardContent className="p-6"><TeamPanel /></CardContent></Card>

      {/* ── API anahtarları ── */}
      <Card><CardContent className="p-6"><ApiKeysPanel scope="user" /></CardContent></Card>

      {/* ── Faturalandırma & abonelik ── */}
      <Card>
        <CardContent className="p-6">
          <div className="font-bold flex items-center gap-1.5 mb-2"><CreditCard className="w-4 h-4 text-warning-700 dark:text-warning-400" /> Faturalandırma & Abonelik</div>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[12px] text-muted-foreground">
              {p ? <>Paket: <strong>{p.name}</strong> · aylık {tok(p.tokenBudgetUsd)} token, {p.siteLimit} site hakkı.</> : "Paketsiz hesap (ekip kullanımı)."}
              <div className="mt-0.5">Kart ile otomatik ödeme yakında; şu an abonelikler yönetici tarafından tanımlanıyor.</div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled className="gap-1.5"><CreditCard className="w-3.5 h-3.5" /> Kart Bağla (yakında)</Button>
              {data.profile.subscriptionActive && (
                <Button size="sm" variant="outline" className="border-error-300 text-error-500" onClick={() => void cancelSub()}>Aboneliği İptal Et</Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Ajans markası: paylaşılan müşteri raporlarında görünür (white-label) ── */}
      <Card>
        <CardContent className="p-6">
          <div className="font-bold mb-1.5">Ajans Markası</div>
          <p className="text-[12px] text-muted-foreground mb-3">
            Müşterinle paylaştığın rapor linklerinde Geolop yerine <strong>senin markan</strong> görünür.
          </p>
          <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto_auto]">
            <input value={brandName} onChange={(e) => setBrandName(e.target.value)} placeholder="Ajans adı"
              className="h-9 rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none dark:border-gray-700 dark:bg-white/[0.03]" />
            <input value={brandLogo} onChange={(e) => setBrandLogo(e.target.value)} placeholder="Logo adresi (https://...)"
              className="h-9 rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none dark:border-gray-700 dark:bg-white/[0.03]" />
            <input type="color" value={brandColor} onChange={(e) => setBrandColor(e.target.value)} title="Marka rengi"
              className="h-9 w-12 cursor-pointer rounded-lg border border-gray-300 dark:border-gray-700" />
            <Button size="sm" disabled={brandSaving} onClick={async () => {
              setBrandSaving(true);
              try {
                const r = await fetch("/api/me/branding", { method: "PATCH", headers: { "content-type": "application/json" },
                  body: JSON.stringify({ name: brandName, logoUrl: brandLogo, color: brandColor }) });
                const j = await r.json();
                if (!r.ok) throw new Error(j.error ?? "Kaydedilemedi");
                toast({ title: "Ajans markası kaydedildi", description: "Bundan sonra paylaşacağın rapor linklerinde markan görünecek." });
              } catch (e) { toast({ title: String(e instanceof Error ? e.message : e), variant: "destructive" }); }
              finally { setBrandSaving(false); }
            }}>{brandSaving ? "Kaydediliyor…" : "Kaydet"}</Button>
          </div>
        </CardContent>
      </Card>

      {/* ── Tehlikeli bölge: hesabı kalıcı sil (KVKK unutulma hakkı) ── */}
      <Card className="border-error-200 dark:border-error-500/30">
        <CardContent className="p-6">
          <div className="font-bold text-error-600 dark:text-error-400 mb-1.5">Hesabı Sil</div>
          <p className="text-[12px] text-muted-foreground mb-3">
            Hesabın, tüm sitelerin ve bütün analiz verilerin <strong>geri dönüşsüz</strong> silinir.
            Onaylamak için kutuya <strong>SİL</strong> yaz.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              value={delConfirm}
              onChange={(e) => setDelConfirm(e.target.value)}
              placeholder="SİL"
              className="h-9 w-28 rounded-lg border border-error-300 bg-white px-3 text-center text-sm outline-none dark:border-error-500/40 dark:bg-white/[0.03]"
            />
            <Button size="sm" variant="outline" disabled={delConfirm !== "SİL" || deleting}
              className="border-error-300 text-error-600"
              onClick={async () => {
                setDeleting(true);
                try {
                  const r = await fetch("/api/me/delete-account", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ confirm: "SİL" }) });
                  const j = await r.json();
                  if (!r.ok) throw new Error(j.error ?? "Silinemedi");
                  logout();
                } catch (e) { toast({ title: String(e instanceof Error ? e.message : e), variant: "destructive" }); setDeleting(false); }
              }}>
              {deleting ? "Siliniyor…" : "Hesabımı Kalıcı Olarak Sil"}
            </Button>
          </div>
          <p className="mt-3 text-[11px] text-muted-foreground">
            <a href="/gizlilik" className="underline">Gizlilik / KVKK Aydınlatma Metni</a> · <a href="/kosullar" className="underline">Kullanım Koşulları</a>
          </p>
        </CardContent>
      </Card>

      <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
        <Gauge className="w-3.5 h-3.5" /> Token hakkı ve özellik kotaları son 30 günlük kayan pencereyle hesaplanır — eski kullanımlar pencereden çıktıkça hakkın yeniden açılır.
      </p>
    </div>
  );
}

/* ── EKİP — üyeler Planlama'da Yazar/Sorumlu seçiminde ve asistan atamalarında görünür ── */
type TeamMember = { id: number; name: string; email: string; role: string };
const TEAM_ROLES = ["yazar", "editör", "teknik", "tasarım", "diğer"];
const roleColor = (r: string) => ({ yazar: "#16a34a", "editör": "#d97706", teknik: "#70706c", "tasarım": "#0891b2" }[r] ?? "#a3a3a0");

function TeamPanel() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ["me-team"],
    queryFn: async (): Promise<{ team: TeamMember[] }> => { const r = await fetch("/api/me/team"); return r.ok ? r.json() : { team: [] }; },
  });
  const team = data?.team ?? [];
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("yazar");
  const [busy, setBusy] = useState(false);
  const inputCls = "h-9 rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none dark:border-gray-700 dark:bg-white/[0.03]";
  const refetch = () => queryClient.invalidateQueries({ queryKey: ["me-team"] });

  const add = async () => {
    if (!name.trim() || !email.trim()) return;
    setBusy(true);
    try {
      const r = await fetch("/api/me/team", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ name, email, role }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error((j as { error?: string }).error ?? "Eklenemedi");
      toast({ title: `${name.trim()} ekibe eklendi` });
      setName(""); setEmail(""); refetch();
    } catch (e) { toast({ title: String(e instanceof Error ? e.message : e), variant: "destructive" }); }
    finally { setBusy(false); }
  };
  const remove = async (m: TeamMember) => {
    await fetch(`/api/me/team/${m.id}`, { method: "DELETE" });
    toast({ title: `${m.name} ekipten çıkarıldı` });
    refetch();
  };

  return (
    <div>
      <div className="font-bold flex items-center gap-1.5 mb-1"><Users className="w-4 h-4 text-warning-700 dark:text-warning-400" /> Ekip</div>
      <p className="text-[12px] text-muted-foreground mb-3">
        Ekip üyeleri <strong>Planlama</strong>'daki Yazar/Sorumlu seçimlerinde görünür; Geolop Asistanı görev atarken buradan seçer.
        E-posta bildirimleri, e-posta altyapısı kurulunca bu adreslere gidecek.
      </p>

      {team.length > 0 && (
        <div className="space-y-1.5 mb-4">
          {team.map((m) => (
            <div key={m.id} className="flex items-center gap-2.5 rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-800">
              <span className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold text-white shrink-0" style={{ background: roleColor(m.role) }}>
                {m.name[0]?.toUpperCase()}
              </span>
              <span className="text-[13px] font-medium min-w-0 truncate">{m.name}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0" style={{ background: `${roleColor(m.role)}1f`, color: roleColor(m.role) }}>{m.role}</span>
              <span className="text-[12px] text-muted-foreground flex-1 truncate">{m.email}</span>
              <button onClick={() => void remove(m)} title="Ekipten çıkar" className="p-1 rounded hover:bg-muted text-muted-foreground shrink-0"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
      {team.length === 0 && <div className="text-[12px] text-muted-foreground mb-4">Henüz ekip üyesi yok — aşağıdan ekle.</div>}

      <div className="grid gap-2 sm:grid-cols-[1fr_1.2fr_auto_auto]">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ad Soyad" className={inputCls} />
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="e-posta@ornek.com" type="email" className={inputCls} />
        <select value={role} onChange={(e) => setRole(e.target.value)} className={inputCls}>
          {TEAM_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <Button size="sm" className="bg-brand-500 hover:bg-brand-600 h-9 gap-1" disabled={busy || !name.trim() || !email.trim()} onClick={() => void add()}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Ekle
        </Button>
      </div>
    </div>
  );
}
